import { useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      const dest = location.state?.from || "/member/hub";
      navigate(dest, { replace: true });
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Sign-in failed.");
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Sign in to your Member Hub" subtitle="Access templates, tools and community forums." testid="login-page">
      <form data-testid="login-form" onSubmit={submit} className="space-y-5">
        <Field label="Email" testid="login-email-field"><input data-testid="login-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} /></Field>
        <Field label="Password" testid="login-password-field"><input data-testid="login-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} /></Field>
        <button data-testid="login-submit" disabled={loading} type="submit" className="btn-primary w-full justify-center">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</> : <>Sign in <ArrowRight className="w-4 h-4" /></>}
        </button>
        <div className="text-sm text-center pt-1">
          <Link to="/forgot-password" data-testid="login-forgot-password-link" className="text-slate-600 hover:text-brand-orange">Forgot your password?</Link>
        </div>
        <div className="text-sm text-slate-600 text-center pt-2">Not a member yet? <Link to="/register" className="text-brand-orange font-semibold">Create an account</Link></div>
      </form>
    </AuthShell>
  );
}

export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setLoading(true);
    try {
      await register(form.name, form.email, form.password);
      navigate("/member/hub", { replace: true });
      toast.success("Welcome to the Member Hub.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Registration failed.");
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Create your Member Hub account" subtitle="Free tier gives you preview access. Upgrade any time." testid="register-page">
      <form data-testid="register-form" onSubmit={submit} className="space-y-5">
        <Field label="Full name" testid="reg-name-field"><input data-testid="register-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></Field>
        <Field label="Email" testid="reg-email-field"><input data-testid="register-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} /></Field>
        <Field label="Password (min 8)" testid="reg-password-field"><input data-testid="register-password" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} /></Field>
        <button data-testid="register-submit" disabled={loading} type="submit" className="btn-primary w-full justify-center">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating account…</> : <>Create account <ArrowRight className="w-4 h-4" /></>}
        </button>
        <div className="text-sm text-slate-600 text-center pt-2">Already a member? <Link to="/login" className="text-brand-orange font-semibold">Sign in</Link></div>
      </form>
    </AuthShell>
  );
}

const inputCls = "w-full px-4 py-3 border border-slate-300 bg-white text-slate-900 text-sm focus:outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue rounded-sm";

export function ForgotPassword() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Something went wrong.");
    } finally { setLoading(false); }
  };

  if (sent) {
    return (
      <AuthShell title="Check your inbox" subtitle="If an account exists for that email, we've sent a password reset link." testid="forgot-password-sent">
        <div className="flex flex-col items-center text-center py-4">
          <CheckCircle2 className="w-10 h-10 text-brand-orange mb-4" />
          <p className="text-sm text-slate-600 leading-relaxed">The link will expire in 30 minutes. If you don't see the email in a few minutes, check your spam folder.</p>
          <Link to="/login" className="btn-secondary mt-8" data-testid="forgot-password-back-to-login">Back to sign in</Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Reset your password" subtitle="Enter your account email and we'll send you a reset link." testid="forgot-password-page">
      <form data-testid="forgot-password-form" onSubmit={submit} className="space-y-5">
        <Field label="Email" testid="forgot-email-field">
          <input data-testid="forgot-password-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
        </Field>
        <button data-testid="forgot-password-submit-button" disabled={loading} type="submit" className="btn-primary w-full justify-center">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Send reset link <ArrowRight className="w-4 h-4" /></>}
        </button>
        <div className="text-sm text-slate-600 text-center pt-2">Remembered it? <Link to="/login" className="text-brand-orange font-semibold">Back to sign in</Link></div>
      </form>
    </AuthShell>
  );
}

export function ResetPassword() {
  const { resetPassword } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (form.password !== form.confirm) { toast.error("Passwords do not match."); return; }
    setLoading(true);
    try {
      await resetPassword(token, form.password);
      toast.success("Password updated. Please sign in.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Reset failed.");
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <AuthShell title="Invalid reset link" subtitle="This password reset link is missing or malformed." testid="reset-password-invalid">
        <div className="text-center">
          <p className="text-sm text-slate-600 leading-relaxed mb-6">Please request a new link from the forgot password page.</p>
          <Link to="/forgot-password" className="btn-primary" data-testid="reset-password-request-new">Request new link</Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Choose a new password" subtitle="Enter your new password below. Minimum 8 characters." testid="reset-password-page">
      <form data-testid="reset-password-form" onSubmit={submit} className="space-y-5">
        <Field label="New password" testid="reset-password-field">
          <input data-testid="reset-password-input" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
        </Field>
        <Field label="Confirm new password" testid="reset-password-confirm-field">
          <input data-testid="reset-password-confirm-input" type="password" required minLength={8} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={inputCls} />
        </Field>
        <button data-testid="reset-password-submit-button" disabled={loading} type="submit" className="btn-primary w-full justify-center">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Updating…</> : <>Update password <ArrowRight className="w-4 h-4" /></>}
        </button>
      </form>
    </AuthShell>
  );
}


function Field({ label, testid, children }) {
  return (
    <label data-testid={testid} className="block">
      <span className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-600 mb-2 inline-block">{label}</span>
      {children}
    </label>
  );
}

function AuthShell({ title, subtitle, testid, children }) {
  return (
    <div data-testid={testid} className="min-h-[calc(100vh-80px)] bg-slate-50 py-20">
      <div className="max-w-[440px] mx-auto px-6">
        <div className="bg-white border border-slate-200 p-10 shadow-sm">
          <div className="eyebrow mb-3">Member Hub</div>
          <h1 className="font-serif text-2xl text-brand-blue mb-2 leading-tight">{title}</h1>
          <p className="text-sm text-slate-600 mb-8">{subtitle}</p>
          {children}
        </div>
        <div className="text-center text-xs text-slate-500 mt-6">Public visitors: <Link to="/contact" className="underline">Contact Us</Link> instead</div>
      </div>
    </div>
  );
}
